import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import ReactMarkdown from "react-markdown";
import { PageShell } from "@/components/site/page-shell";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/blog/$slug")({
  component: PostPage,
  loader: async ({ params }) => {
    const { data } = await supabase
      .from("posts")
      .select("title,excerpt,cover_url,published_at,created_at,updated_at")
      .eq("slug", params.slug)
      .eq("published", true)
      .maybeSingle();
    return { post: data };
  },
  head: ({ params, loaderData }) => {
    const p = loaderData?.post;
    const title = p?.title ? `${p.title} — CoderLity Blog` : `${params.slug} — CoderLity Blog`;
    const description = p?.excerpt ?? undefined;
    const image = p?.cover_url ?? undefined;
    const datePublished = p?.published_at ?? p?.created_at ?? undefined;
    const dateModified = p?.updated_at ?? datePublished;
    const meta: Array<Record<string, string>> = [
      { title },
      { property: "og:title", content: title },
      { property: "og:url", content: `/blog/${params.slug}` },
      { property: "og:type", content: "article" },
    ];
    if (description) {
      meta.push({ name: "description", content: description });
      meta.push({ property: "og:description", content: description });
    }
    if (image) {
      meta.push({ property: "og:image", content: image });
      meta.push({ name: "twitter:image", content: image });
    }
    const scripts = p
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              headline: p.title,
              description: p.excerpt ?? undefined,
              image: image ? [image] : undefined,
              datePublished,
              dateModified,
              mainEntityOfPage: `/blog/${params.slug}`,
            }),
          },
        ]
      : undefined;
    return {
      meta,
      links: [{ rel: "canonical", href: `https://coderlity.com/blog/${params.slug}` }],
      ...(scripts ? { scripts } : {}),
    };
  },
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-32 text-center">
        <h1 className="text-3xl font-bold">Post not found</h1>
        <Link to="/blog" search={{ q: "", cat: "", page: 1, view: "grid" }} className="mt-6 inline-flex items-center gap-2 text-primary">
          <ArrowLeft className="h-4 w-4" /> Back to blog
        </Link>
      </div>
    </PageShell>
  ),
  errorComponent: ({ error }) => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-32 text-center">
        <h1 className="text-2xl font-bold">Something went wrong</h1>
        <p className="mt-2 text-muted-foreground">{error.message}</p>
      </div>
    </PageShell>
  ),
});

function PostPage() {
  const { slug } = Route.useParams();
  const { data: post, isLoading } = useQuery({
    queryKey: ["public", "post", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("posts")
        .select("*")
        .eq("slug", slug)
        .eq("published", true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <PageShell>
        <div className="mx-auto max-w-3xl px-4 py-32 text-center text-muted-foreground">Loading…</div>
      </PageShell>
    );
  }
  if (!post) throw notFound();

  const date = post.published_at ?? post.created_at;

  return (
    <PageShell>
      <article className="mx-auto max-w-3xl px-4 lg:px-8 py-16">
        <Link to="/blog" search={{ q: "", cat: "", page: 1, view: "grid" }} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
          <ArrowLeft className="h-4 w-4" /> All posts
        </Link>
        <h1 className="mt-6 text-4xl sm:text-5xl font-bold tracking-tight">{post.title}</h1>
        <p className="mt-3 text-sm text-muted-foreground">{format(new Date(date), "MMMM d, yyyy")}</p>
        {post.cover_url && (
          <img src={post.cover_url} alt={post.title} className="mt-8 w-full rounded-2xl border border-border" />
        )}
        {post.excerpt && <p className="mt-8 text-lg text-muted-foreground">{post.excerpt}</p>}
        <div className="mt-8 prose prose-invert max-w-none prose-headings:tracking-tight prose-a:text-primary">
          <ReactMarkdown>{post.content || ""}</ReactMarkdown>
        </div>
      </article>
    </PageShell>
  );
}
